import moment from 'moment';
import {
  addDoc,
  arrayUnion,
  collection,
  collectionGroup,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  startAfter,
  Transaction,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { storage } from '../../firebase/config';
import _ from 'lodash';
import { getDatabase, push, ref } from 'firebase/database';
import {
  deleteObject,
  getBlob,
  getDownloadURL,
  getMetadata,
  getStorage,
  listAll,
  ref as storageRef
} from "firebase/storage";
import { Collections } from "@core/constants/FirebasePaths";
import { DocumentChange, QuerySnapshot } from "@firebase/firestore";
import { usePopulateCacheStore } from "@shared/store/PopulateCache.store";
import { useUserSessionStore } from "../../stores/UserSession.store";

export interface IQuery {
  type: 'where' | 'orderBy' | 'limit' | 'startAfter';
  field?: string;
  operator?: '!=' | '==' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'in' | 'array-contains-any' | 'not-in';
  value?: any;
  direction?: 'asc' | 'desc';
  limit?: number;
}

interface IProps<T> {
  collectionPath?: string;
  documentPath?: string;
  queries?: IQuery[];
  loadData?: boolean;
  initialPopulate?: {
    fields: string[];
    subFields?: string[];
    subSubFields?: string[];
  };
  processData?: (data: T[]) => T[];
}

export const useFirebaseData = <DataType>({
  collectionPath,
  documentPath,
  queries = [],
  loadData = true,
  initialPopulate,
  processData
}: IProps<DataType>) => {
  const db = getFirestore();
  const auth = getAuth();
  const batch = writeBatch(db);
  const [data, setData] = useState<DataType[]>([]);
  const [collectionSize, setCollectionSize] = useState();
  const [indexedData, setIndexedData] = useState<Record<string, DataType>>({});
  const [docListener, setDocListener] = useState<DataType>();
  const [batchOperations, setBatchOperations] = useState([]);
  const [fetched, setFetched] = useState(false);
  const user = useUserSessionStore(state => state.user);
  let unsubscribe;
  const indexedItems = usePopulateCacheStore(state => state.indexedItems);
  const addIndexItem = usePopulateCacheStore(state => state.addIndexItem);
  let cacheItems = indexedItems;

  const getCollectionRef = (path?: string) => {
    return collection(db, path || collectionPath);
  };

  const getReference = (path: string) => {
    return doc(db, path);
  };

  const docsCount = async (queries: IQuery[] = [], distinctCollectionPath?: string) => {
    const coll = await getQueryRef(queries, collection(db, distinctCollectionPath || collectionPath));
    const snapshot = await getCountFromServer(coll);
    return snapshot.data().count;
  };

  const updateBatch = (itemId: string, data, distinctCollectionPath?: string) => {
    const documentRef = doc(db, distinctCollectionPath || collectionPath, itemId);
    setBatchOperations(prev => [...prev, { type: 'update', ref: documentRef, data }]);
  };

  const clearBatchOperations = () => {
    setBatchOperations([]);
  };

  const commitBatchOperations = async () => {
    batchOperations.forEach(operation => {
      if (operation.type === 'update') {
        batch.update(operation.ref, operation.data);
      }
    });
    await batch.commit();
    clearBatchOperations();
  };

  const updateWithBatch = async (items: any[], data, distinctCollectionPath?: string) => {
    const batch = writeBatch(db);

    items.forEach((item: any) => {
      const documentRef = doc(db, distinctCollectionPath || collectionPath, item.id);
      batch.update(documentRef, { ...item, ...data });
    });

    return batch.commit();
  };

  const docWithId = async <Type>(path: string): Promise<Type> => {
    const docRef = doc(db, path);
    const resp = await getDoc(docRef);

    return resp.exists() ? { ...resp.data(), id: resp.id, path } as Type : null;
  };

  const set = async (id: string, data: any, newCollectionPath?: string) => {
    const documentRef = doc(db, newCollectionPath || collectionPath, id);
    await setDoc(documentRef, data);

    return documentRef;
  };

  const add = async (data: any, newCollectionPath?: string) => {
    const colRef = newCollectionPath ? collection(db, newCollectionPath) : getCollectionRef();

    return addDoc(colRef, {
      createdAt: Date.now(),
      createdBy: getReference(`${Collections.users._path}/${user?.id}`),
      trash: false,
      ...data
    });
  };

  const update = async (id: string, data: any, path?: string) => {
    delete data['id'];

    const documentRef = doc(db, path || collectionPath, id);

    return updateDoc(documentRef, {
      ...data,
      updatedAt: Date.now(),
      updatedBy: user ? getReference(`${Collections.users._path}/${user.id}`) : null
    });
  };

  const trash = async (id: string, path?: string) => {
    const documentRef = doc(db, path || collectionPath, id);

    return updateDoc(documentRef, {
      trash: true,
      deletedAt: Date.now(),
      deletedBy: getReference(`${Collections.users._path}/${user.id}`)
    });
  };

  const collectionWithIds = async <Type>(queries: IQuery[] = [], distinctCollectionPath?: string): Promise<Type[]> => {
    const snapshot = await getDocs(getQueryRef(queries, collection(db, distinctCollectionPath || collectionPath)));
    return snapshot.docs.map((doc: any) => ({
      ...doc.data(),
      id: doc.id,
      path: doc.ref.path
    } as any as Type));
  };

  const collectionWithLastVisible = async <Type>(queries: IQuery[] = [], distinctCollectionPath?: string) => {
    const snapshot = await getDocs(getQueryRef(queries, collection(db, distinctCollectionPath || collectionPath)));
    const documents = snapshot.docs.map((doc: any) => ({
      ...doc.data(),
      id: doc.id,
      path: doc.ref.path
    }));

    return {
      documents,
      lastVisible: snapshot.docs[snapshot.docs.length - 1]
    };
  };

  const collectionGroupWithIds = async <Type>(path: string, queries: IQuery[]): Promise<Type[]> => {
    const snapshot: any = await getDocs(getQueryRef(queries, collectionGroup(db, path)));
    return snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id, path: doc.ref.path } as any as Type));
  };

  const listenCollectionGroup = async <Type>(path: string, queries: IQuery[]) => {
    unsubscribe = onSnapshot(getQueryRef(queries, collectionGroup(db, path)), (snapshot) => {
      setData(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id, path: doc.ref.path } as any as Type)));
      setFetched(true);
    }, (error) => {
      console.error(error);
    });
  };

  const listenCollectionByChanges = async (path: string, queries: IQuery[]) => {
    unsubscribe = onSnapshot(getQueryRef(queries, collection(db, path)), (snapshot: QuerySnapshot) => {
      setData(snapshot.docChanges()
        .map((change: DocumentChange) => {
          if (change.type === 'removed') return null;

          return {
            ...change.doc.data(),
            id: change.doc.id,
            path: change.doc.ref.path,
            changeType: change.type
          } as any as DataType;
        })
        .filter(Boolean)
      );
      setFetched(true);
    }, (error) => {
      console.error(error);
    });
  };

  const listenCollectionGroupByChanges = async (path: string, queries: IQuery[]) => {
    unsubscribe = onSnapshot(getQueryRef(queries, collectionGroup(db, path)), (snapshot: QuerySnapshot) => {
      setData(snapshot.docChanges()
        .map((change: DocumentChange) => {
          if (change.type === 'removed') return null;

          return {
            ...change.doc.data(),
            id: change.doc.id,
            path: change.doc.ref.path,
            changeType: change.type
          } as any as DataType;
        })
        .filter(Boolean)
      );
      setFetched(true);
    }, (error) => {
      console.error(error);
    });
  };

  const listenCollection = (queries: IQuery[] = [], collectionPath?: string) => {
    const queryRef = collectionPath ? getQueryRef(queries, collection(db, collectionPath)) : getQueryRef(queries);

    unsubscribe = onSnapshot(queryRef, (snapshot) => {
      setData(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id, path: doc.ref.path })));
      setFetched(true);
    });
  };

  const listenDocument = (documentId: string, queries: IQuery[] = [], altCollectionPath?: string) => {
    if (unsubscribe) unsubscribe();
    const docRef = doc(db, altCollectionPath ? altCollectionPath : collectionPath, documentId);

    unsubscribe = onSnapshot(getQueryRef(queries, docRef), (snapshot) => {
      if (!snapshot.data()) {
        setDocListener(null);
        return null;
      }

      setDocListener({
        ...snapshot.data(),
        id: snapshot.id,
        path: snapshot.ref.path
      });
      setFetched(true);
    }, (error) => {
      console.error(error);
    });
  };

  const listenFromPath = (path: string) => {
    if (unsubscribe) unsubscribe();
    const docRef = doc(db, path);

    unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (!snapshot.exists()) return setDocListener(null);

      setDocListener({
        ...snapshot.data(),
        id: snapshot.id,
        path: snapshot.ref.path
      } as DataType);
      setFetched(true);
    }, (error) => {
      console.error(error);
    });
  };

  const getQueryRef = (queries: IQuery[] = [], distinctCollectionRef?) => {
    let queryRef: any = query(distinctCollectionRef || getCollectionRef());
    queries.forEach((queryItem) => {
      if (queryItem.type === 'where') {
        queryRef = query(queryRef, where(queryItem.field, queryItem.operator, queryItem.value));
      } else if (queryItem.type === 'orderBy') {
        queryRef = query(queryRef, orderBy(queryItem.field, queryItem.direction));
      } else if (queryItem.type === 'limit') {
        queryRef = query(queryRef, limit(queryItem.limit));
      } else if (queryItem.type === 'startAfter') {
        queryRef = query(queryRef, startAfter(queryItem.value));
      }
    });

    return queryRef;
  };

  const uploadFile = async (file: any, path: string): Promise<string> => {
    return new Promise(resolve => {
      const currentFileRef = storage.ref(path);

      currentFileRef.put(file).then(async (snapshot) => {
        const url = await snapshot.ref.getDownloadURL();
        resolve(url);
      });
    });
  };

  const uploadFileByBase64 = async (base64: any, path: string, onProgress?: (progress: number) => void): Promise<string> => {
    return new Promise((resolve, reject) => {
      const currentFileRef = storage.ref(path);
      const uploadFile = currentFileRef.putString(base64, 'data_url');

      uploadFile.on('state_changed', (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) onProgress(progress);
      }, (error) => {
        reject(error);
      }, async () => {
        const url = await uploadFile.snapshot.ref.getDownloadURL();
        resolve(url);
      });
    });
  };

  const getFileBlob = (path: string) => {
    const fileRef = path.startsWith("https://") ? storage.refFromURL(path) : storage.ref(path);
    return getBlob(fileRef);
  };

  const getFileMetadata = (path: string) => {
    const fileRef = path.startsWith("https://") ? storage.refFromURL(path) : storage.ref(path);
    return getMetadata(fileRef);
  };

  const getFile = async (path: string) => {
    const metadata = await getFileMetadata(path);
    const blob = await getFileBlob(path);
    const file = new File([blob], metadata.name, { type: blob.type });
    file['preview'] = URL.createObjectURL(file);
    file['uploadedAt'] = moment(metadata.timeCreated).valueOf();
    return file;
  };

  const getFileBase64 = async (path: string) => {
    const blob = await getFileBlob(path);

    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const getFileArray = async (path: string, filter?: { from: number, to: number }) => {
    const storage = getStorage();
    const folderRef = storageRef(storage, path);

    try {
      const items = await listAll(folderRef);
      const fileList = await Promise.all(
        items.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return getFile(url);
        })
      );

      return _.orderBy(fileList, 'uploadedAt', 'desc');
    } catch (error) {
      console.error(`Error al obtener lista de documentos ${path}`, error);
      return [];
    }
  };

  const getFileRef = (path: string) => {
    const storage = getStorage();
    const pathFromUrl = new URL(path).pathname;
    return storageRef(storage, pathFromUrl);
  };

  const getFileRefArray = async (path: string, filter?: { from: number, to: number }) => {
    const storage = getStorage();
    const folderRef = storageRef(storage, path);

    try {
      const items = await listAll(folderRef);
      const fileList = await Promise.all(
        items.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          const metaData = await getMetadata(itemRef);
          return { url: url, uploadedAt: moment(metaData.timeCreated).valueOf() };
        })
      );

      return _.orderBy(fileList, 'uploadedAt', 'desc');
    } catch (error) {
      console.error(`Error al obtener lista de referencias de documentos ${path}`, error);
      return [];
    }
  };

  const deleteFile = async (path: string) => {
    const metadata = await getFileMetadata(path);
    const storage = getStorage();
    const fileRef = storageRef(storage, metadata.fullPath);
    try {
      await deleteObject(fileRef);
    } catch (e) {
      console.error(`Hubo un error al borrar el archivo ${metadata.name}`);
    }
  };

  const populateByCacheFirst: any = async (items: any[], fields: string[], subFields: string[] = [], subSubFields: string[] = []) => {
    const populatedItems = [];

    for (const item of items) {
      populatedItems.push(await populateByItem(item, fields, subFields, subSubFields, true));
    }

    return populatedItems;
  };

  const populate: any = async (items: any[], fields: string[], subFields: string[] = [], subSubFields: string[] = []) => {
    return await Promise.all(items.map(async item => await populateByItem(item, fields, subFields, subSubFields)));
  };

  const populateByItem = async (item, fields: string[], subFields: string[] = [], subSubFields: string[] = [], cacheFirst = false) => {
    for (let field of fields) {
      if (item?.[field]) {
        try {
          const itemPath = item[field]?.path;

          if (itemPath) {
            if (cacheFirst) {
              const cacheItem = cacheItems[itemPath];

              item[field] = cacheItem || await docWithId(itemPath);

              if (!cacheItem) {
                addIndexItem(itemPath, item[field]);
                cacheItems[itemPath] = item[field];
              }
            } else {
              item[field] = await docWithId(itemPath);
            }
          }

          for (let subField of subFields) {
            if (item[field][subField]) {
              if (item[field][subField].path) item[field][subField] = await docWithId(item[field][subField].path);

              for (let subSUbField of subSubFields) {
                if (item[field][subField][subSUbField]) {
                  if (item[field][subField][subSUbField].path) item[field][subField][subSUbField] = await docWithId(item[field][subField][subSUbField].path);
                }
              }
            }
          }
        } catch (e) {
          console.error(`Hubo un error al obtener el documento del campo "${field}" para el path ${item[field]?.path}`, e);
        }
      }
    }
    return item;
  };

  const populateArrayFieldByItem = async (item: any, fields: string[]) => {
    for (let field of fields) {
      const array = _.get(item, field);
      if (array) {
        try {
          let populatedArray = (await Promise.all(array.map(async (itemField: any) => {
            return await docWithId(itemField.path);
          })));

          populatedArray = populatedArray.filter((item: any) => !!item);

          _.set(item, field, populatedArray);
        } catch (e) {
          console.error(`Hubo un error al obtener el documento del campo ${field.toUpperCase()}`, e);
        }
      }
    }
    return item;
  };

  const populateArraySubFieldByItem = async (item: any, arrayFields: string[], subFields: string[]) => {
    for (let i = 0; i < arrayFields.length; i++) {
      let arrayField = arrayFields[i];
      const array = _.get(item, arrayField);
      if (array) {
        try {
          let populatedArray = (await Promise.all(array.map(async (itemField: any) => {
            return {
              ...itemField,
              [subFields[i]]: await docWithId(itemField[subFields[i]].path)
            };
          })));

          populatedArray = populatedArray.filter((item: any) => !!item[subFields[i]]);

          _.set(item, arrayField, populatedArray);
        } catch (e) {
          console.error(`Hubo un error al obtener el documento del campo ${arrayField.toUpperCase()}`, e);
        }
      }
    }
    return item;
  };

  const populateArrayByNestedField = async (items: any[], arrayField: string[], subFields: string[]) => {
    return await Promise.all(items.map(async item => await populateArraySubFieldByItem(item, arrayField, subFields)));
  };

  const populateArrayField = async (items: any[], fields: string[]) => {
    return await Promise.all(items.map(async item => await populateArrayFieldByItem(item, fields)));
  };

  const populateArray = async (items: any[]) => {
    return await Promise.all(items.map(async item =>
      item.path
        ? await docWithId(item.path)
        : item
    ));
  };

  const populateArrayByCacheFirst = async (items: any[]) => {
    let populatedItems = [];

    for (const item of items) {
      if (!item.path) {
        populatedItems.push(item);
        continue;
      }

      const cacheItem = cacheItems[item.path];

      const dataItem = cacheItem || await docWithId(item.path);

      populatedItems.push(dataItem);

      if (!cacheItem) {
        addIndexItem(item.path, dataItem);
        cacheItems[item.path] = dataItem;
      }
    }

    return populatedItems;
  };

  const populateArrayOfArray = async (items: any[], fields: string[]) => {
    for (let item of items) {
      for (let field of fields) {
        if (!!item?.[field]) item[field] = await populateArray(item[field]);
      }
    }
    return items;
  };

  const populateArrayOfArrayByCacheFirst = async (items: any[], fields: string[]) => {
    for (let item of items) {
      for (let field of fields) {
        if (!!item?.[field]) item[field] = await populateArrayByCacheFirst(item[field]);
      }
    }
    return items;
  };

  const createId = () => {
    const database = getDatabase();
    const dataRef = ref(database, 'ids');
    const newChild = push(dataRef);

    return newChild.key;
  };

  const generateBaseDocument = async (path: string) => {
    return set('', {}, path);
  };

  const updateSequenceData = async (refPath: string, handleSequence: any) => {
    const NUMBER_OF_ATTEMPTS = 1;
    let sequenceRef = getReference(refPath);

    const attemptTransaction = async (attempt: number) => {
      try {
        if (attempt <= 0) return;

        return await runTransaction(db, async (transaction) => {
          let doc = await transaction.get(sequenceRef);

          if (!doc.exists()) {
            console.log('Document does not exist! Creating base document...');
            await generateBaseDocument(refPath);

            sequenceRef = getReference(refPath);
            doc = await transaction.get(sequenceRef);
            if (!doc.exists()) throw new Error("Can't create base document");
          }

          const newValue = handleSequence(doc.data());
          transaction.update(sequenceRef, newValue);

          return newValue;
        });
      } catch (e) {
        console.error("Hubo un problema al intentar obtener consecutivo, reintentando...", e);
        return await attemptTransaction(attempt - 1);
      }
    };
    return await attemptTransaction(NUMBER_OF_ATTEMPTS);
  };

  const subscribeCollection = (queries: IQuery[] = []) => {
    unsubscribe = onSnapshot(getQueryRef(queries), async (snapshot: any) => {
      let items = snapshot.docs.map((doc: any) => ({
        ...doc.data(),
        id: doc.id,
        path: doc.ref.path
      }));

      if (initialPopulate) items = await populate(items, initialPopulate.fields, initialPopulate.subFields || [], initialPopulate.subSubFields || []);
      if (processData) items = processData(items);

      setData(items);
      setFetched(true);
    });
  };

  const listenCollectionSize = (queries: IQuery[] = [], distinctCollectionPath?: string) => {
    if (unsubscribe) unsubscribe();

    unsubscribe = onSnapshot(getQueryRef(queries, collection(db, distinctCollectionPath || collectionPath)), (snapshot: any) => {
      setCollectionSize(snapshot.size);
    });
  };

  const transaction = async (handleTransaction: (transaction: Transaction) => any) => {
    return runTransaction(db, async (transaction) => {
      return handleTransaction(transaction);
    });
  };

  useEffect(() => {
    if (loadData) {
      collectionPath && subscribeCollection(queries);
      if (documentPath) {
        const docId = documentPath.split('/').pop();
        const docPath = documentPath.split('/').slice(0, -1).join('/');
        listenDocument(docId, queries, docPath);
      }
    }
  }, [loadData, documentPath]);

  useEffect(() => {
    setIndexedData(_.keyBy(data, "id"));
  }, [data]);

  useEffect(() => {
    return () => {
      return unsubscribe && unsubscribe();
    };
  }, []);

  return {
    add,
    set,
    update,
    trash,
    arrayUnion,
    deleteField,
    collectionWithIds,
    listenCollection,
    docs: data,
    data,
    indexedData,
    docListener,
    listenFromPath,
    updateBatch,
    commitBatchOperations,
    unsubscribe,
    updateWithBatch,
    uploadFile,
    getFileBlob,
    getFileMetadata,
    getFile,
    getFileBase64,
    getFileArray,
    getFileRef,
    getFileRefArray,
    deleteFile,
    getReference,
    docsCount,
    docWithId,
    populate,
    populateByCacheFirst,
    populateByItem,
    populateArray,
    populateArrayOfArray,
    populateArrayOfArrayByCacheFirst,
    populateArrayField,
    populateArrayFieldByItem,
    populateArrayByNestedField,
    auth,
    collectionGroupWithIds,
    collectionWithLastVisible,
    listenCollectionGroup,
    uploadFileByBase64,
    createId,
    updateSequenceData,
    listenDocument,
    listenCollectionGroupByChanges,
    listenCollectionByChanges,
    fetched,
    listenCollectionSize,
    collectionSize,
    transaction,
    getDocs,
    getQueryRef,
    getCollectionRef
  };
};
